import { useCallback } from 'react';
import type { GameConfig } from '@/engine/types';
import type { GuestProfile } from '@/net/room';
import { useRoomStore, getRemoteSeats } from '@/net/roomStore';
import {
  clearRoomError,
  handSeatToAi,
  hostRoom,
  joinRoom,
  leaveRoom,
  playTurnForSeat,
  recoverableJoin,
  recoverableRoom,
  probeRoom,
  reopenRoom,
  retryConnection,
  startHostedGame,
} from '@/net/session';

/**
 * Room session state + actions. `status`/`seats` re-render live; the
 * actions are stable. `remoteSeats()` gives the guests as PlayerConfigs so
 * the lobby can append them to the host's local seats on Start.
 */
export function useRoom() {
  const state = useRoomStore();
  const host = useCallback(() => hostRoom(), []);
  const join = useCallback(
    (code: string, profile: GuestProfile) => joinRoom(code, profile),
    []
  );
  const leave = useCallback(() => leaveRoom(), []);
  const startGame = useCallback(
    (config: GameConfig) => startHostedGame(config),
    []
  );
  const clearError = useCallback(() => clearRoomError(), []);
  return {
    status: state.status,
    role: state.role,
    code: state.code,
    hostName: state.hostName,
    variantId: state.variantId,
    seats: state.seats,
    selfPlayerId: state.selfPlayerId,
    error: state.error,
    connection: state.connection,
    hostPresent: state.hostPresent,
    blockedNotice: state.blockedNotice,
    joinStage: state.joinStage,
    joinHint: state.joinHint,
    diag: state.diag,
    probeRoom,
    playTurnForSeat,
    handSeatToAi,
    retryConnection,
    reopenRoom,
    recoverableRoom,
    recoverableJoin,
    isHosting: state.role === 'host' && state.status !== 'idle',
    host,
    join,
    leave,
    startGame,
    clearError,
    remoteSeats: getRemoteSeats,
  };
}
